// Unified brand: 206Gravel.
//
// The business now operates as a single brand — 206Gravel — across the
// entire Puget Sound metro. All three domains (253gravel.com, 206gravel.com,
// 425gravel.com) still serve the same React app, but every visitor now sees
// the unified 206Gravel identity regardless of which domain they arrived on.
//
// `getRegion()` therefore always resolves to '206'. The 253/425 metadata is
// retained in REGIONS only so any historical lookups by code still work, but
// nothing in the UI should branch on region anymore — coverage across 206,
// 425, and 253 is communicated through copy/reviews instead of per-domain
// theming.
export type RegionCode = '253' | '206' | '425'

export interface RegionMeta {
  code: RegionCode
  /** Human-readable area name, e.g. "Seattle". */
  name: string
  /** Short descriptor used in copy, e.g. "Seattle proper". */
  label: string
  /** Representative cities for the region. */
  cities: string[]
  /** Map center coordinates [lat, lng] for the region. */
  center: [number, number]
  /** Default delivery zone for the region. */
  defaultZone: number
}

export const REGIONS: Record<RegionCode, RegionMeta> = {
  '253': {
    code: '253',
    name: 'South Sound',
    label: 'Pierce / South Sound',
    cities: ['Tacoma', 'Puyallup', 'Lakewood', 'Federal Way'],
    center: [47.2529, -122.4443], // Tacoma
    defaultZone: 1,
  },
  '206': {
    code: '206',
    name: 'Seattle',
    label: 'Seattle proper',
    cities: ['Seattle', 'West Seattle', 'Mercer Island'],
    center: [47.6062, -122.3321], // Seattle
    defaultZone: 3,
  },
  '425': {
    code: '425',
    name: 'Eastside',
    label: 'Eastside',
    cities: ['Bellevue', 'Redmond', 'Kirkland', 'Issaquah', 'Sammamish'],
    center: [47.6101, -122.2015], // Bellevue
    defaultZone: 2,
  },
}

/**
 * Resolve the active region.
 *
 * Unified brand: ALWAYS '206'. Regardless of the hostname (253gravel.com /
 * 425gravel.com / 206gravel.com) or any `?region=` override, every visitor
 * sees the single 206Gravel identity — same logo, same wordmark, same copy.
 * Coverage across 206/425/253 is now expressed through content (reviews,
 * service-area copy) rather than per-domain theming.
 */
export function getRegion(): RegionCode {
  return '206'
}

/** Convenience accessor for the active region's metadata. */
export function getRegionMeta(): RegionMeta {
  return REGIONS[getRegion()]
}
